import React from "react";
import { cx } from "utils/join-classnames";
import { requestUpdatePackage } from "core/services";
import { useSessionContext } from "ui/context/SessionContext";
import usePermissions from "ui/hooks/usePermissions";
import { useNotifications } from "ui/context/NotificationsContext";
import { Icon, Button, Tooltip, ConfirmActionModal } from "ui/components";
import { PlanKey } from "core/models";
import styles from "./Plan.module.scss";

type Feature =
    | "Ingests"
    | "Team environments"
    | "Stream Recording time"
    | "Max Clipping Length"
    | "Video storage"
    | "Create clips"
    | "Download Clips"
    | "Upload Video Files"
    | "Edit Clips"
    | "Watch livestream PiP"
    | "Custom clip formats"
    | "Manual captioning"
    | "Create compilations"
    | "Share to Social Media"
    | "Publish to private platforms"
    | "Custom integrations"
    | "Live support";

interface Plan {
    header: {
        key: PlanKey;
        title: string;
        price: string;
        billing: "monthly" | "yearly" | "";
    };
    body: Record<Feature, string | boolean>;
}

const features: Record<Feature | "spacer", string | null> = {
    Ingests: null,
    "Team environments": null,
    "Stream Recording time": null,
    "Max Clipping Length": null,
    "Video storage": null,
    spacer: null,
    "Create clips": null,
    "Download Clips": null,
    "Upload Video Files": null,
    "Edit Clips":
        "Personalize clips by cropping, rescaling,  trimming, adding intros, outros and/or overlays. ",
    "Custom clip formats": "export clips in formats other than mp4",
    "Share to Social Media": "twitter, youtube, facebook",
    "Watch livestream PiP": null,
    "Create compilations": null,
    "Manual captioning": null,
    "Publish to private platforms": "FTP, AWS S3",
    "Custom integrations":
        "Importing/exporting video from/to your own platform",
    "Live support": null,
};

const availablePlans: Plan[] = [
    {
        header: {
            key: "basic",
            title: "Basic",
            price: "€199",
            billing: "monthly",
        },
        body: {
            Ingests: "1",
            "Team environments": "1",
            "Stream Recording time": "24 hours",
            "Max Clipping Length": "30 min",
            "Video storage": "7 days",
            "Create clips": true,
            "Download Clips": true,
            "Watch livestream PiP": false,
            "Upload Video Files": true,
            "Edit Clips": true,
            "Custom clip formats": true,
            "Manual captioning": false,
            "Create compilations": false,
            "Share to Social Media": true,
            "Publish to private platforms": false,
            "Custom integrations": false,
            "Live support": false,
        },
    },
    {
        header: {
            key: "advanced",
            title: "Advanced",
            price: "€299",
            billing: "monthly",
        },
        body: {
            Ingests: "1",
            "Team environments": "1",
            "Stream Recording time": "24 hours",
            "Max Clipping Length": "30 min",
            "Video storage": "30 days",
            "Create clips": true,
            "Download Clips": true,
            "Watch livestream PiP": true,
            "Upload Video Files": true,
            "Edit Clips": true,
            "Custom clip formats": true,
            "Manual captioning": true,
            "Create compilations": true,
            "Share to Social Media": true,
            "Publish to private platforms": false,
            "Custom integrations": false,
            "Live support": false,
        },
    },
    {
        header: {
            key: "custom",
            title: "Enterprise",
            price: `Let's talk`,
            billing: "",
        },
        body: {
            Ingests: "No limit",
            "Team environments": "No limit",
            "Stream Recording time": "Up to 30 days",
            "Max Clipping Length": "Up to 90 min",
            "Video storage": "t.b.d.",
            "Create clips": true,
            "Download Clips": true,
            "Watch livestream PiP": true,
            "Upload Video Files": true,
            "Edit Clips": true,
            "Custom clip formats": true,
            "Manual captioning": true,
            "Create compilations": true,
            "Share to Social Media": true,
            "Publish to private platforms": true,
            "Custom integrations": true,
            "Live support": true,
        },
    },
];

const SettingsPlan: React.FC = () => {
    const sessionContext = useSessionContext();
    const userPermissions = usePermissions();
    const [currentPlan, setCurrentPlan] = React.useState<string>();
    const [currentPlanIdx, setCurrentPlanIdx] = React.useState<number>(0);
    const [action, setAction] = React.useState<PlanKey | null>(null);
    const [actionType, setActionType] = React.useState<
        "upgrade" | "downgrade" | null
    >(null);
    const { addNotification } = useNotifications();
    React.useEffect(() => {
        if (!sessionContext.selectedProject || !sessionContext.session) {
            return;
        }
        setCurrentPlan(sessionContext.selectedProject.package);
        setCurrentPlanIdx(
            availablePlans.findIndex(
                (plan) =>
                    plan.header.key === sessionContext.selectedProject?.package,
            ),
        );
    }, [sessionContext]);

    const handleAction = (planKey: PlanKey): void => {
        setAction(planKey);
        const planIdx = availablePlans.findIndex(
            (plan) => plan.header.key === planKey,
        );
        setActionType(planIdx > currentPlanIdx ? "upgrade" : "downgrade");
    };
    const handleUpdatePlanRequest = async (): Promise<void> => {
        try {
            if (!sessionContext.session) {
                throw Error("Missing session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("Missing selected project");
            }
            if (!action) {
                throw Error("Missing Action");
            }
            const result = await requestUpdatePackage(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                action,
            );
            if (result.error || !result.value) {
                throw Error(result.error);
            }
            addNotification(
                "success",
                "Request sent!",
                `We'll get back to you a.s.a.p.`,
                0,
            );
            setAction(null);
        } catch (error) {
            addNotification(
                "error",
                "somthing went wrong",
                "Please try again or contact us",
                0,
            );
        }
    };
    const targetPlanTitle = availablePlans.find((p) => p.header.key === action)
        ?.header.title;

    return (
        <>
            <div className={styles.heading}>
                <div className={styles.heading__left}>
                    <h1 className={cx(styles.heading, styles.heading__primary)}>
                        Plan settings
                    </h1>
                    <span className={styles.heading__desc}>
                        Up- or down-grade your plan
                    </span>
                </div>
            </div>
            <div className={styles.planTable}>
                <div className={styles.planTable__header}>
                    <div className={styles.planTable__header__cell} />
                    <div className={styles.planTable__spacer} />
                    {availablePlans.map((plan, planIdx) => (
                        <React.Fragment key={plan.header.key}>
                            <div
                                className={cx(
                                    styles.planTable__header__cell,
                                    plan.header.key === currentPlan
                                        ? styles.active
                                        : null,
                                )}
                            >
                                <div className={styles.planTable__header__flex}>
                                    <div>{plan.header.title}</div>
                                    <div
                                        className={
                                            plan.header.price.length >= 7
                                                ? styles.smallerPrice
                                                : undefined
                                        }
                                    >
                                        {plan.header.price}
                                    </div>
                                    <div>
                                        {plan.header.billing !== "" &&
                                            `(Billed ${plan.header.billing})`}
                                    </div>
                                    {userPermissions.isProjectAdmin && (
                                        <div>
                                            {planIdx === currentPlanIdx && (
                                                <span
                                                    className={
                                                        styles.currentplan
                                                    }
                                                >
                                                    Current plan
                                                </span>
                                            )}
                                            {planIdx > currentPlanIdx &&
                                                plan.header.key !==
                                                    "custom" && (
                                                    <Button
                                                        type="primary"
                                                        theme="light"
                                                        onClick={() =>
                                                            handleAction(
                                                                plan.header.key,
                                                            )
                                                        }
                                                    >
                                                        Upgrade
                                                    </Button>
                                                )}
                                            {planIdx > currentPlanIdx &&
                                                plan.header.key ===
                                                    "custom" && (
                                                    <Button
                                                        type="primary"
                                                        theme="dark"
                                                        onClick={() =>
                                                            handleAction(
                                                                plan.header.key,
                                                            )
                                                        }
                                                    >
                                                        Contact us
                                                    </Button>
                                                )}
                                            {planIdx < currentPlanIdx && (
                                                <Button
                                                    className={styles.downgrade}
                                                    onClick={() =>
                                                        handleAction(
                                                            plan.header.key,
                                                        )
                                                    }
                                                    role="presentation"
                                                    type="secondary"
                                                    theme="dark"
                                                >
                                                    Downgrade
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div
                                className={styles.planTable__spacer}
                                key={`${plan.header.key}_spacer`}
                            />
                        </React.Fragment>
                    ))}
                </div>
                <div className={styles.planTable__body}>
                    {Object.keys(features).map((feature) => {
                        if (feature === "spacer") {
                            return (
                                <div
                                    className={
                                        styles.planTable__body__rowSpacer
                                    }
                                    key={feature.toString()}
                                >
                                    <div
                                        className={styles.planTable__body__cell}
                                    />
                                    <div className={styles.planTable__spacer} />
                                    {availablePlans.map((plan) => (
                                        <>
                                            <div
                                                className={cx(
                                                    styles.planTable__body__cell,
                                                    plan.header.key ===
                                                        currentPlan
                                                        ? styles.active
                                                        : null,
                                                )}
                                                key={plan.header.key}
                                            />
                                            <div
                                                className={
                                                    styles.planTable__spacer
                                                }
                                            />
                                        </>
                                    ))}
                                </div>
                            );
                        }
                        return (
                            <div
                                className={styles.planTable__body__row}
                                key={feature.toString()}
                            >
                                <div className={styles.planTable__body__cell}>
                                    {feature}
                                    {features[feature as Feature] && (
                                        <Tooltip
                                            text={
                                                features[feature as Feature] ||
                                                ""
                                            }
                                            className={
                                                styles.planTable__body__infoIcon
                                            }
                                        >
                                            <Icon name="info" />
                                        </Tooltip>
                                    )}
                                </div>
                                <div className={styles.planTable__spacer} />
                                {availablePlans.map((plan) => (
                                    <React.Fragment key={plan.header.key}>
                                        <div
                                            className={cx(
                                                styles.planTable__body__cell,
                                                plan.header.key === currentPlan
                                                    ? styles.active
                                                    : null,
                                            )}
                                        >
                                            {typeof plan.body[
                                                feature as Feature
                                            ] === "string" &&
                                                plan.body[feature as Feature]}
                                            {typeof plan.body[
                                                feature as Feature
                                            ] === "boolean" && (
                                                <Icon
                                                    name={
                                                        plan.body[
                                                            feature as Feature
                                                        ]
                                                            ? "confirm"
                                                            : "close"
                                                    }
                                                    className={
                                                        plan.body[
                                                            feature as Feature
                                                        ]
                                                            ? styles.green
                                                            : undefined
                                                    }
                                                />
                                            )}
                                        </div>
                                        <div
                                            className={styles.planTable__spacer}
                                            key={`${plan.header.key}_spacer`}
                                        />
                                    </React.Fragment>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
            {action && (
                <ConfirmActionModal
                    theme="light"
                    title="Changing plans?"
                    description={`Are you sure you want to ${actionType} your plan to ${targetPlanTitle}?`}
                    confirmBtnText="Yes"
                    handleCancel={() => {
                        setAction(null);
                    }}
                    handleConfirm={() => {
                        handleUpdatePlanRequest();
                    }}
                />
            )}
        </>
    );
};

export default SettingsPlan;
